// src/components/Layout/Layout.tsx
import React, { useEffect, useState } from 'react';
import Sidebar from '../Sidebar/Sidebar';
import Navbar from '../Navbar/Navbar';

interface LayoutProps {
  children: React.ReactNode;
}

// 🔥 Componente interno que usa el tema
const LayoutContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isMobileViewport = () =>
    typeof window !== 'undefined' && window.innerWidth < 768;
  const shouldCollapseSidebar = () =>
    typeof window !== 'undefined' && window.innerWidth < 992;
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const [sidebarOpen, setSidebarOpen] = useState(() => !shouldCollapseSidebar());

  useEffect(() => {
    const mobileMediaQuery = window.matchMedia('(max-width: 767.98px)');
    const compactMediaQuery = window.matchMedia('(max-width: 991.98px)');
    const handleViewportChange = () => {
      setIsMobile(mobileMediaQuery.matches);
      setSidebarOpen(!compactMediaQuery.matches);
    };

    mobileMediaQuery.addEventListener('change', handleViewportChange);
    compactMediaQuery.addEventListener('change', handleViewportChange);
    return () => {
      mobileMediaQuery.removeEventListener('change', handleViewportChange);
      compactMediaQuery.removeEventListener('change', handleViewportChange);
    };
  }, []);

  useEffect(() => {
    if (!isMobile || !sidebarOpen) return;

    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSidebarOpen(false);
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMobile, sidebarOpen]);

  return (
    <div className="relative min-h-screen bg-canvas text-content-primary">
      
      <div className="relative z-10">
        <Sidebar
          isOpen={sidebarOpen}
          isMobile={isMobile}
          setIsOpen={setSidebarOpen}
        />
        {isMobile && sidebarOpen && (
          <button
            aria-label="Cerrar menú de navegación"
            className="fixed inset-0 z-20 bg-overlay"
            onClick={() => setSidebarOpen(false)}
            type="button"
          />
        )}
        <div className={`ml-0 transition-[margin] duration-300 ${sidebarOpen ? 'md:ml-[var(--navigation-sidebar-width)]' : 'md:ml-[var(--navigation-sidebar-collapsed-width)]'}`}>
          <Navbar
            isSidebarOpen={sidebarOpen}
            onMenuToggle={() => setSidebarOpen((isOpen) => !isOpen)}
          />
          <main >
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

// 🔥 Layout principal (sin usar useTheme directamente)
const Layout: React.FC<LayoutProps> = ({ children }) => {
  return <LayoutContent>{children}</LayoutContent>;
};

export default Layout;
